import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import { isMobileDevice, openAppOrStore } from "@constants/mobileApp";

// Routes where we never nag the user toward the app. /sign is a public,
// account-less signing flow for external clients/artisans — they must be able
// to sign on the web without installing anything. (Universal links still open
// the app there automatically if it happens to be installed.)
const EXCLUDED_PREFIXES = ["/sign"];

const DISMISS_KEY = "mbmo_app_banner_dismissed";

/**
 * Bottom banner shown on phones across the whole web app, inviting the user to
 * open / install the native app. The web stays fully usable — this is a nudge,
 * not a redirect. Dismissed for the session via sessionStorage.
 *
 * Only shown to a fully-authenticated user (`authed`): someone who has created
 * their login method AND has an active backend profile. We never nudge a
 * person who hasn't set up their connection yet (login / activation / reset
 * screens, orphan accounts) toward the app — they'd hit the same login wall
 * there and have no way through.
 */
export function MobileAppBanner({ authed }: { authed: boolean }) {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    const alreadyDismissed =
      sessionStorage.getItem(DISMISS_KEY) === "1";
    setDismissed(alreadyDismissed || !isMobileDevice());
  }, []);

  const onExcludedRoute = EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p));
  if (!authed || dismissed || onExcludedRoute) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-neutral-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] px-4 py-3 flex flex-row items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-neutral-900 flex items-center justify-center flex-shrink-0">
        <Icon1 name="smartphone" size={20} color="white" />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-h3 font-semibold text-neutral-800 truncate">
          Ouvrir dans l'application
        </span>
        <span className="text-h5 text-neutral-500 truncate">
          Une meilleure expérience sur mobile
        </span>
      </div>
      <button
        onClick={openAppOrStore}
        className="flex-shrink-0 bg-neutral-900 text-white text-h4 font-semibold rounded-lg px-4 py-2 cursor-pointer hover:bg-neutral-800 transition-colors"
      >
        Ouvrir
      </button>
      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-neutral-600 cursor-pointer"
      >
        <Icon1 name="close" size={18} />
      </button>
    </div>
  );
}
